export function makeOverrideUrl(appName: string, branchHash: string): string {
  // The Jenkins pipelines of the micro webapps uploads the built app to this bucket
  // for the given branch.
  const appsBucketBaseUrl = 'https://apps.resources.project44.com';
  return `${appsBucketBaseUrl}/__BRANCH_PREVIEWS__/${appName}/${branchHash}/project44-${appName}.js`;
}

export function getQueryParams(searchQuery: string): Record<string, string> {
  const queryString = searchQuery.slice(1, searchQuery.length); // strip out the leading question mark
  const params: Record<string, string> = {};
  queryString.split('&').forEach((group: string) => {
    const [key, value] = group.split('=');
    params[key] = value;
  });

  return params;
}

/*
  In order to support branch previews, changes in a branch are built and pushed to a GCP bucket.
  We then build a URL with a query param pointing to the location of the built file. If this query
  param is present, we apply the override. We only do this in the integration environment.

  This uses the importMapOverrides API that's set on the window object by the import-map-overrides package: 
  https://github.com/joeldenning/import-map-overrides/blob/2a9f022aa47f684df3a3a63618d88c08f6146ce5/src/api/js-api.js#L76
*/
export function applyBranchPreviewOverrides() {
  const w = window as any;
  if (w._p44_env.ENV !== 'qa-integration' || !w.location.search) {
    return;
  }

  const p44Prefix = '@project44';
  const overridePrefix = 'override_';

  // Build key value object out of query param string
  const params = getQueryParams(w.location.search);

  // Get the current override map
  const currentOverrideMap = w.importMapOverrides.getOverrideMap().imports;

  // Check for the override_=clear query param. This is provided as a convenience for non-dev people to be able
  // to easily clear their overrides without needing to mess with the dev console:
  // https://na14.voc.qa-integration.p-44.com?override_=clear
  if (params[overridePrefix] === 'clear') {
    let overridesWereCleared = false;

    Object.keys(currentOverrideMap)
      .filter((moduleName) => moduleName.startsWith(p44Prefix))
      .forEach((moduleName) => {
        w.importMapOverrides.removeOverride(moduleName);
        overridesWereCleared = true;
      });

    if (overridesWereCleared) {
      // Refresh the page to apply the override removal
      w.location.reload();
    }

    return;
  }

  // Now check for app-specific overrides. Example:
  // https://na14.voc.qa-integration.p-44.com?override_my-app-ui=1e40a168a93642a1dcb1e7aeff221469
  // We assume the person visiting this URL is a non-dev person reviewing the changes for the purposes of
  // accepting a ticket. They might have visited a branch preview URL in the past, but don't want that
  // override to be used anymore. So we remove any other overrides that might still be present from previous
  // visits to a branch preview URL and ensure only the overrides in the query params are applied.

  let changesWereMade = false;
  const overrideKeys = new Set();
  const overrideParams = Object.entries(params).filter(([key]) =>
    key.startsWith(overridePrefix),
  );

  for (const [key, branchHash] of overrideParams) {
    const appName = key.split(overridePrefix)[1];
    const overrideKey = `${p44Prefix}/${appName}`;
    const overrideValue = makeOverrideUrl(appName, branchHash);

    overrideKeys.add(overrideKey);

    if (currentOverrideMap[overrideKey] === overrideValue) {
      continue;
    }

    w.importMapOverrides.addOverride(overrideKey, overrideValue);
    changesWereMade = true;
  }

  // If there is at least one override present in the query params,
  // clear out any project44 apps that aren't in the query params
  if (overrideKeys.size > 0) {
    Object.keys(currentOverrideMap)
      .filter(
        (moduleName) =>
          moduleName.startsWith(p44Prefix) && !overrideKeys.has(moduleName),
      )
      .forEach((moduleName) => {
        w.importMapOverrides.removeOverride(moduleName);
        changesWereMade = true;
      });
  }

  if (changesWereMade) {
    // Refresh the page to apply the overrides
    w.location.reload();
  }
}
